/**
 * journey-web's thin adapter over the shared first-destination decision
 * (W2 §9.2). The decision itself is `resolvePostOnboardingDestination` in
 * `@tonnie/onboarding` — platform-neutral, because mobile has neither a
 * `/testit` route nor an in-app journey builder. This file is the only place
 * that turns it into a wouter path. journey-web has no setup gate, so there is
 * no "onboarding first" destination to route.
 */
import type { PostOnboardingDestination } from '@tonnie/onboarding';

/** A destination the router has no path for is a compile error, not a 404. */
function unreachableDestination(destination: never): never {
  throw new Error(`Unhandled first destination: ${JSON.stringify(destination)}`);
}

/**
 * The route for a destination.
 *
 * `resume-journey` lands on `/dashboard` and NOT on the journey builder:
 * `/journey/builder` creates journeys (see `JourneyBuilderPage`), so sending
 * someone there to "resume" would mint a second one — the exact defect this
 * wave exists to close. The journey it names is made active by
 * `useGoToFirstDestination` before the navigation.
 */
export function firstDestinationHref(destination: PostOnboardingDestination): string {
  switch (destination.kind) {
    case 'checkup':
      return '/checkup';
    case 'testit':
      return '/testit';
    case 'resume-journey':
      return '/dashboard';
    case 'build-journey':
      return '/journey/builder?new=1';
    case 'dashboard':
      return '/dashboard';
    default:
      return unreachableDestination(destination);
  }
}
